import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { UsageDisplay } from '../components/UsageDisplay';
import { useProjects } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { getTierDisplayName } from '../hooks/usePricing';
import { invalidateUsageCache } from '../hooks/useUsage';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';
import {
  Plus,
  FolderOpen,
  Globe,
  Lock,
  MoreVertical,
  Trash2,
  ExternalLink,
  Calendar,
  Crown,
  GitFork,
  CheckCircle,
  X,
} from 'lucide-react';

export function DashboardPage() {
  const { tt, locale } = useI18n();
  const { projects, loading, createProject, deleteProject } = useProjects();
  const { user, profile, subscriptionTier, refreshProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  const [creditsAdded, setCreditsAdded] = useState<number | null>(null);

  const robotEmail = 'robot@llambada.com';
  const isRobotUser =
    (user?.email || '').toLowerCase() === robotEmail ||
    (profile?.email || '').toLowerCase() === robotEmail;
  const isFreeUser = subscriptionTier === 'free' && !isRobotUser;
  const hasReachedProjectLimit = isFreeUser && projects.length >= 1;

  useEffect(() => {
    document.title = tt('Dashboard') + ' - Llambada';
    return () => { document.title = 'Llambada'; };
  }, [tt]);

  // Handle upgrade/credit purchase success redirect from Stripe
  useEffect(() => {
    const isUpgraded = searchParams.get('upgraded') === 'true';
    const creditsParam = searchParams.get('credits_added');

    if (isUpgraded || creditsParam) {
      // Clear the URL params immediately to prevent re-triggering
      searchParams.delete('upgraded');
      searchParams.delete('credits_added');
      setSearchParams(searchParams, { replace: true });

      const handleReturn = async () => {
        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Invalidate usage cache so credits display refreshes
        invalidateUsageCache();
        await refreshProfile();
        if (creditsParam) {
          setCreditsAdded(parseInt(creditsParam, 10) || 0);
        }
        setShowUpgradeSuccess(true);
      };

      handleReturn();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const handleNewProjectClick = () => {
    if (isRobotUser) {
      setShowCreateModal(true);
      return;
    }
    if (hasReachedProjectLimit) {
      setShowUpgradeModal(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    setCreating(true);
    setCreateError(null);
    const result = await createProject(newProjectName, '');
    setCreating(false);
    
    if (result.error) {
      setCreateError(result.error);
      return;
    }
    
    if (result.cloudRunError) {
      // Project was created but Cloud Run setup failed
      // Show warning but still close modal - user can retry from project page
      console.warn('Cloud Run setup failed:', result.cloudRunError);
    }
    
    setShowCreateModal(false);
    setNewProjectName('');
  };

  const handleDeleteProject = async (id: string) => {
    setDeleting(true);
    await deleteProject(id);
    setDeleting(false);
    setShowDeleteModal(null);
  };

  const formatDate = (date: string) => {
    const localeCode = locale === 'zh' ? 'zh-CN' : locale;
    return new Date(date).toLocaleDateString(localeCode, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Upgrade Success Banner */}
        {showUpgradeSuccess && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 animate-slide-up">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-theme-text">
                    {creditsAdded
                      ? tt('{count} credits added!', { count: String(creditsAdded) })
                      : tt('Welcome to {tier}!', { tier: getTierDisplayName(subscriptionTier) })}
                  </h3>
                  <p className="text-sm text-theme-text-secondary">
                    {creditsAdded
                      ? tt('Your credits have been added to your account.')
                      : tt('Your subscription is now active. Enjoy your new features!')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowUpgradeSuccess(false)}
                className="p-1.5 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-surface-hover transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-theme-text">
              {tt('Your Mini-Apps')}
            </h1>
            <p className="mt-1 text-sm sm:text-base text-theme-text-secondary">
              {tt('Create and manage your LLM mini-apps')}
            </p>
          </div>
          <button
            onClick={handleNewProjectClick}
            className="btn btn-primary self-start sm:self-auto"
          >
            <Plus className="h-5 w-5" />
            {tt('New Mini-App')}
          </button>
        </div>

        {/* Usage display - collapsible on mobile */}
        <div className="mb-6 lg:hidden">
          <UsageDisplay compact />
        </div>

        <div className="flex gap-8">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {projects.length === 0 ? (
              <div className="card p-12 text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-surface-hover mb-6">
                  <FolderOpen className="h-8 w-8 text-theme-text-muted" />
                </div>
                <h2 className="font-display text-xl font-semibold text-theme-text mb-2">
                  {tt('No mini-apps yet')}
                </h2>
                <p className="text-theme-text-secondary mb-6 max-w-md mx-auto">
                  {tt('Create your first mini-app to start having conversations with LLMs and organizing your work.')}
                </p>
                <button
                  onClick={handleNewProjectClick}
                  className="btn btn-primary"
                >
                  <Plus className="h-5 w-5" />
                  {tt('Create your first mini-app')}
                </button>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
                {projects.map((project, index) => (
              <div
                key={project.id}
                className="card p-5 hover:border-theme-border transition-all duration-200 group animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <Link
                    to={`/project/${project.id}`}
                    className="flex-1 min-w-0"
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-lg font-semibold text-theme-text truncate group-hover:text-accent-500 transition-colors">
                        {project.name}
                      </h3>
                      {project.forked_from && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-theme-text-muted bg-theme-surface-hover px-1.5 py-0.5 rounded shrink-0">
                          <GitFork className="h-2.5 w-2.5" />
                          {tt('Fork')}
                        </span>
                      )}
                    </div>
                  </Link>
                  <div className="relative ml-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenMenu(openMenu === project.id ? null : project.id);
                      }}
                      className="p-1.5 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-surface-hover transition-colors"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {openMenu === project.id && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenu(null)}
                        />
                        <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-lg bg-theme-surface border border-theme-border shadow-xl py-1">
                          {project.is_published && project.public_id && (
                            <a
                              href={`/p/${project.public_id}${project.slug ? `/${project.slug}` : ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover hover:text-theme-text transition-colors"
                              onClick={() => setOpenMenu(null)}
                            >
                              <ExternalLink className="h-4 w-4" />
                              {tt('View public page')}
                            </a>
                          )}
                          <button
                            onClick={() => {
                              setOpenMenu(null);
                              setShowDeleteModal(project.id);
                            }}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-theme-error hover:bg-theme-error-bg w-full transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                            {tt('Delete mini-app')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <Link to={`/project/${project.id}`}>
                  {project.description && (
                    <p className="text-sm text-theme-text-secondary mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-theme-text-muted">
                    <div className="flex items-center gap-1.5">
                      {project.is_published ? (
                        <>
                          <Globe className="h-3.5 w-3.5 text-theme-success" />
                          <span className="text-theme-success">{tt('Published')}</span>
                        </>
                      ) : (
                        <>
                          <Lock className="h-3.5 w-3.5" />
                          <span>{tt('Private')}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{formatDate(project.updated_at)}</span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
          </div>

          {/* Sidebar - visible on desktop only */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24">
              <UsageDisplay />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={tt('Create new mini-app')}
      >
        <form onSubmit={handleCreateProject} className="space-y-4">
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-theme-text-secondary mb-2">
              {tt('Mini-app name')}
            </label>
            <input
              id="projectName"
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              className="input"
              placeholder={tt('My awesome mini-app')}
              required
              autoFocus
            />
          </div>
          {createError && (
            <div className="text-sm text-theme-error bg-theme-error-bg border border-theme-error-border rounded-lg p-3">
              {createError}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                setCreateError(null);
              }}
              className="btn btn-secondary flex-1"
            >
              {tt('Cancel')}
            </button>
            <button
              type="submit"
              disabled={creating || !newProjectName.trim()}
              className="btn btn-primary flex-1"
            >
              {creating ? <LoadingSpinner size="sm" /> : tt('Create mini-app')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        title={tt('Delete mini-app')}
        size="sm"
      >
        <p className="text-theme-text-secondary mb-6">
          {tt('Are you sure you want to delete this mini-app? This action cannot be undone and all conversations will be permanently lost.')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleteModal(null)}
            className="btn btn-secondary flex-1"
          >
            {tt('Cancel')}
          </button>
          <button
            onClick={() => showDeleteModal && handleDeleteProject(showDeleteModal)}
            disabled={deleting}
            className="btn btn-danger flex-1"
          >
            {deleting ? <LoadingSpinner size="sm" /> : tt('Delete')}
          </button>
        </div>
      </Modal>

      {/* Upgrade Modal for Project Limit */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title={tt('Upgrade to Create More Mini-Apps')}
        size="sm"
      >
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-4">
            <Crown className="h-7 w-7 text-amber-500" />
          </div>
          <p className="text-theme-text-secondary mb-2">
            {tt('Free accounts are limited to 1 mini-app.')}
          </p>
          <p className="text-sm text-theme-text-muted mb-6">
            {PAYMENTS_DISABLED
              ? PAYMENTS_DISABLED_MESSAGE
              : tt('Upgrade to the Standard plan to create unlimited mini-apps and unlock more features.')}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="btn btn-secondary flex-1"
            >
              {tt('Maybe Later')}
            </button>
            {!PAYMENTS_DISABLED && (
              <Link
                to="/settings"
                onClick={() => setShowUpgradeModal(false)}
                className="btn btn-primary flex-1"
              >
                {tt('View Plans')}
              </Link>
            )}
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
